import { useCallback, useMemo } from 'react';
import { message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import communityAPI from '../api';
import { normalizeListResponse } from '../api/normalizers';

const DEFAULT_METADATA_PAGE_SIZE = 20;

const fetchAllRecords = async (fetchFunction, params) => {
  const collected = [];
  const baseParams = { ...params };
  let page = baseParams.page ?? 1;

  while (true) {
    const requestParams = {
      ...baseParams,
      page,
    };

    const response = await fetchFunction(requestParams);
    const normalized = normalizeListResponse(response.data);
    collected.push(...normalized.results);

    if (!normalized.next || normalized.results.length === 0) {
      break;
    }
    page += 1;
  }

  return collected;
};

export const useMetadata = ({
  categoriesParams = {},
  tagsParams = {},
  enabled = true
} = {}) => {
  const normalizedCategoryParams = useMemo(
    () => ({ page_size: DEFAULT_METADATA_PAGE_SIZE, ...categoriesParams }),
    [categoriesParams]
  );

  const normalizedTagParams = useMemo(
    () => ({ page_size: DEFAULT_METADATA_PAGE_SIZE, ...tagsParams }),
    [tagsParams]
  );

  const categoriesQuery = useQuery({
    queryKey: ['community', 'categories', normalizedCategoryParams],
    enabled,
    queryFn: async () => {
      if (Object.prototype.hasOwnProperty.call(categoriesParams, 'page')) {
        const response = await communityAPI.categories.getAll(normalizedCategoryParams);
        const normalized = normalizeListResponse(response.data);
        return normalized.results;
      }
      return fetchAllRecords(communityAPI.categories.getAll, normalizedCategoryParams);
    },
    staleTime: 60_000,
    onError: () => {
      message.error('Failed to fetch categories');
    }
  });

  const tagsQuery = useQuery({
    queryKey: ['community', 'tags', normalizedTagParams],
    enabled,
    queryFn: async () => {
      if (Object.prototype.hasOwnProperty.call(tagsParams, 'page')) {
        const response = await communityAPI.tags.getAll(normalizedTagParams);
        const normalized = normalizeListResponse(response.data);
        return normalized.results;
      }
      return fetchAllRecords(communityAPI.tags.getAll, normalizedTagParams);
    },
    staleTime: 60_000,
    onError: () => {
      message.error('Failed to fetch tags');
    }
  });

  const refetch = useCallback(async () => {
    const [categoriesResult, tagsResult] = await Promise.all([
      categoriesQuery.refetch(),
      tagsQuery.refetch()
    ]);
    return {
      categories: categoriesResult.data ?? [],
      tags: tagsResult.data ?? []
    };
  }, [categoriesQuery, tagsQuery]);

  return {
    categories: categoriesQuery.data ?? [],
    tags: tagsQuery.data ?? [],
    loading: categoriesQuery.isPending || tagsQuery.isPending,
    refetch
  };
};

export default useMetadata;
